import { atom, useAtom } from 'jotai';
import { Node } from '../types/cytoscape';
import { useNodeHighlighting } from './useNodeHighlighting';
import { Core, NodeSingular } from 'cytoscape';
import { useCallback, useRef } from 'react';

// 検索結果のノードを管理するアトム
const searchedNodesAtom = atom<Node[]>([]);
const isSearchDropdownOpenAtom = atom(false);

export const useNodeSearch = () => {
  const [searchedNodes, setSearchedNodes] = useAtom(searchedNodesAtom);
  const [isDropdownOpen, setIsDropdownOpen] = useAtom(isSearchDropdownOpenAtom);
  const { highlightNodeAndConnections, removeHighlighting } =
    useNodeHighlighting();

  // アニメーション停止用のフラグを管理
  const animationStopRef = useRef<boolean>(false);

  /**
   * 検索結果のノードをハイライト表示する（Cytoscapeアニメーション付き）
   * @returns マッチしたノードの配列（Node[]形式）
   */
  const highlightSearchResults = useCallback(
    (cy: Core | null, topicTagsList: readonly string[]): Node[] => {
      if (!cy) return [];

      // 既存のアニメーションを停止
      animationStopRef.current = true;
      cy.nodes().stop(true, false); // すべてのアニメーションを停止
      cy.nodes().removeClass('searched');
      cy.nodes().removeClass('faded');
      cy.nodes().style('opacity', 1); // opacityをリセット

      // labelに一致するノードを検索
      const matchedNodes = cy.nodes().filter((ele: NodeSingular) => {
        const label = ele.data('label');
        return typeof label === 'string' && topicTagsList.includes(label);
      });

      // マッチしたノードをNode[]形式に変換
      const nodeArray: Node[] = [];
      matchedNodes.forEach((ele: NodeSingular) => {
        nodeArray.push(ele.data() as Node);
      });

      // マッチしたノードにアニメーション
      if (matchedNodes && matchedNodes.length > 0) {
        matchedNodes.addClass('searched');
        matchedNodes.style('display', 'element');

        // 点滅アニメーションを開始
        animationStopRef.current = false;
        const blinkAnimation = () => {
          if (animationStopRef.current) return;

          matchedNodes
            .animate({
              style: { opacity: 0.3 },
              duration: 500,
              easing: 'ease-in-out',
            })
            .animate({
              style: { opacity: 1 },
              duration: 500,
              easing: 'ease-in-out',
              complete: () => {
                if (!animationStopRef.current) {
                  blinkAnimation(); // 再帰的にループ
                }
              },
            });
        };
        blinkAnimation();
      }

      return nodeArray;
    },
    []
  );

  /**
   * 検索結果をクリアする
   */
  const clearSearchResults = useCallback(
    (cy: Core | null) => {
      if (!cy) return;

      // アニメーションを停止
      animationStopRef.current = true;
      cy.nodes().stop(true, false);
      cy.nodes().removeClass('searched');
      cy.nodes().removeClass('faded');
      cy.nodes().style('opacity', 1);

      // 検索結果をクリア
      setSearchedNodes([]);
      setIsDropdownOpen(false);
    },
    [setSearchedNodes, setIsDropdownOpen]
  );

  /**
   * Cytoscapeインスタンスに触れず、検索状態のみをリセットする
   */
  const resetSearchState = useCallback(() => {
    setSearchedNodes([]);
    setIsDropdownOpen(false);
  }, [setSearchedNodes, setIsDropdownOpen]);

  return {
    searchedNodes,
    setSearchedNodes,
    isDropdownOpen,
    setIsDropdownOpen,
    highlightSearchResults,
    clearSearchResults,
    resetSearchState,
    highlightNodeAndConnections,
    removeHighlighting,
  };
};
